import { Circle, CircleCheck, Minus, X } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { Glare } from "@src/components/animation/Glare";
import type { ProgressStep } from "@src/components/steps/StepRows";

/** The icon plate: the row's height, and where the rail is drawn from. */
const PLATE = 22;
/** The work log's gap between rows, which the rail crosses. */
const WORK_LOG_GAP = 12;

/**
 * Figma "Atlas / Work-log" (151:285): steps on a rail.
 *
 * Each row is a 22px plate with the step's 14px icon centred, 8px before a
 * single Body 2 line. A rail joins the plates, drawn between them rather than
 * behind them, so the plate needs no background to hide it and the timeline
 * sits on whatever surface holds it. A running row draws a lighter glyph; a
 * queued row is dimmed.
 *
 * `renderStep` lets a caller wrap a row, such as with debug details. `gap`
 * spaces the rows further where the steps are the whole content.
 */
export const StepTimeline = ({
  steps,
  gap = WORK_LOG_GAP,
  renderStep = (_step, row) => row,
}: {
  steps: ProgressStep[];
  gap?: number;
  renderStep?: (step: ProgressStep, row: ReactNode) => ReactNode;
}) => (
  <Stack gap={`${gap}px`} sx={{ width: "100%" }}>
    {steps.map((step, index) => (
      <Fragment key={step.id}>
        {renderStep(
          step,
          <TimelineRow
            step={step}
            gap={gap}
            last={index === steps.length - 1}
          />
        )}
      </Fragment>
    ))}
  </Stack>
);

const TimelineRow = ({
  step,
  gap,
  last,
}: {
  step: ProgressStep;
  gap: number;
  last: boolean;
}) => {
  const running = step.status === "running";
  const Icon =
    step.status === "failed"
      ? X
      : step.status === "skipped"
        ? Minus
        : (step.icon ?? (step.status === "done" ? CircleCheck : Circle));
  const row = (
    <Stack direction="row" alignItems="center" gap={2}>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={(theme) => ({
          color:
            step.status === "failed"
              ? theme.palette.error.main
              : running
                ? IgCoreTextColors.placeholder
                : theme.igIconColors.light,
          flexShrink: 0,
          height: PLATE,
          overflow: "hidden",
          width: PLATE,
        })}
      >
        <Icon size={14} aria-hidden />
      </Stack>
      <IgTypography
        variant="body2"
        sx={{
          color: IgCoreTextColors.primary,
          fontWeight: 400,
          lineHeight: "18px",
          minWidth: 0,
        }}
      >
        {step.label}
        {step.detail && (
          <Box component="span" sx={{ color: IgCoreTextColors.secondary }}>
            {" "}
            — {step.detail}
          </Box>
        )}
      </IgTypography>
    </Stack>
  );
  return (
    <Stack
      direction="row"
      data-testid="step"
      data-status={step.status}
      sx={{
        minHeight: PLATE,
        position: "relative",
        opacity: step.status === "pending" ? 0.45 : 1,
      }}
    >
      {running ? <Glare>{row}</Glare> : row}
      {!last && (
        <Box
          aria-hidden
          sx={(theme) => ({
            backgroundColor: theme.igBorders.core.default,
            bottom: -gap,
            left: PLATE / 2 - 0.5,
            position: "absolute",
            top: PLATE,
            width: "1px",
          })}
        />
      )}
    </Stack>
  );
};
